/**
 * Voice AI Service
 * Handles communication with the voice-ai Supabase Edge Function.
 * Supports both full voice mode (STT + LLM + TTS) and dictation-only mode.
 */
import { supabase } from './supabase';
import { getWorkspaceIdForAi } from './database';
import {
  isRetryableAiError, friendlyAiBusyMessage, RETRY_DELAYS_MS,
  redactSecrets, isAiAuthError, friendlyAiAuthMessage, errorToText,
  detailFromAiErrorBody, isAiAllowanceError, friendlyAiAllowanceMessage,
} from './aiRetryHelpers';

/**
 * Build-time kill switch for the whole voice feature. Voice UI is hidden
 * entirely unless VITE_VOICE_ENABLED is exactly "true".
 *
 * VOICE IS CURRENTLY OFF, ON PURPOSE. The self-hosted TTS server
 * (cosy.yarder.app) has returned HTTP 530 since ~2026-06-27, and there is no
 * fallback synthesizer by design. With voice visible, tapping the mic recorded
 * the contractor, spent Whisper and Gemini tokens on the round trip, played a
 * canned greeting from the local phrase cache, and then went silent for the
 * actual answer — the worst of both worlds: full cost, no output, and it looks
 * broken rather than disabled.
 *
 * This flag existed but had ZERO call sites, so it gated nothing; the mic
 * appeared for anyone whose browser exposed getUserMedia. It is now wired into
 * both mic buttons (AIChatView, AIChatPanel).
 *
 * TO TURN VOICE BACK ON: restore the TTS server, then set
 * VITE_VOICE_ENABLED=true in the Vercel project and redeploy. It is a
 * build-time variable, so a redeploy is required — a dashboard toggle alone
 * will not do it.
 */
export function isVoiceEnabled(): boolean {
  return String(import.meta.env.VITE_VOICE_ENABLED ?? '').toLowerCase() === 'true';
}

export type VoiceMode = 'full' | 'dictation';

export interface VoiceAIResponse {
  transcript: string;
  reply: string;
  /**
   * @deprecated The voice-ai edge function no longer returns audio; TTS happens
   *   in the browser via {@link import('./browserTTS').BrowserTTS}.
   *   Field kept for backward-compat with old client builds; will be undefined.
   */
  audioBase64?: string | null;
  language?: string;
  error?: string;
}

/**
 * Convert a Blob (audio recording) to a base64 string
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      // Strip the data:audio/webm;base64, prefix
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Send a voice message to the AI assistant.
 * Returns the transcription, AI reply, and optionally the spoken audio.
 * Uses OpenAI Whisper for STT, Gemini for the reply, OpenAI TTS for the spoken audio.
 */
export async function sendVoiceMessage(
  audioBlob: Blob,
  history: { role: string; text: string }[],
  context: Record<string, unknown>,
  mode: VoiceMode = 'full',
  voice: string = 'nova'
): Promise<VoiceAIResponse> {
  const audioBase64 = await blobToBase64(audioBlob);
  let lastDetail = '';

  // Same retry policy as the text chat path — Gemini overload, brief network
  // blips, and Edge Function cold-starts all show up as transient 5xx
  // responses that succeed on retry. Audio blob is encoded once, reused
  // across attempts (re-encoding would burn ~50ms each retry for nothing).
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke('voice-ai', {
        body: { audio: audioBase64, history, context, mode, voice, workspaceId: getWorkspaceIdForAi() },
      });

      // Every error string on this path is redacted before it is logged or
      // returned. The voice pipeline authenticates against Groq (Whisper
      // primary) and OpenAI (fallback) as well as Gemini, so a 401 body here
      // can carry a `gsk_` or `sk-` key — and the caller renders this text
      // straight into a chat bubble that is then saved to the transcript.
      if (error) {
        let detail = errorToText(error.message);
        try {
          if (error.context) {
            const body = await error.context.json();
            detail = detailFromAiErrorBody(body, error.message);
          }
        } catch (_) { /* ignore parse errors */ }

        // The monthly limit: no retry, and info not error — see aiRetryHelpers.
        if (isAiAllowanceError(detail)) {
          console.info('[VoiceAI] monthly AI limit reached; not retrying');
          return { transcript: '', reply: '', error: friendlyAiAllowanceMessage() };
        }

        if (isRetryableAiError(detail) && attempt < RETRY_DELAYS_MS.length) {
          console.warn(`[VoiceAI] retryable error on attempt ${attempt + 1}, retrying in ${RETRY_DELAYS_MS[attempt]}ms:`, redactSecrets(detail));
          lastDetail = detail;
          await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
          continue;
        }

        console.error('[VoiceAI] Edge Function error:', redactSecrets(detail));
        if (isAiAuthError(detail)) return { transcript: '', reply: '', error: friendlyAiAuthMessage(history) };
        return { transcript: '', reply: '', error: isRetryableAiError(detail) ? friendlyAiBusyMessage(history) : `Voice AI error: ${redactSecrets(detail)}` };
      }

      if (data?.error) {
        const apiDetail = errorToText(data.error);
        if (isRetryableAiError(apiDetail) && attempt < RETRY_DELAYS_MS.length) {
          console.warn(`[VoiceAI] retryable API error on attempt ${attempt + 1}, retrying:`, redactSecrets(apiDetail));
          lastDetail = apiDetail;
          await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
          continue;
        }
        console.error('[VoiceAI] API error:', redactSecrets(apiDetail));
        if (isAiAuthError(apiDetail)) return { transcript: '', reply: '', error: friendlyAiAuthMessage(history) };
        return { transcript: '', reply: '', error: isRetryableAiError(apiDetail) ? friendlyAiBusyMessage(history) : redactSecrets(apiDetail) };
      }

      return {
        transcript: typeof data?.transcript === 'string' ? data.transcript : '',
        reply: typeof data?.reply === 'string' && data.reply.trim() ? data.reply : 'No response received.',
        audioBase64: data?.audioBase64 || null,
        language: typeof data?.language === 'string' ? data.language : 'en',
      };
    } catch (err: unknown) {
      const message = errorToText(err instanceof Error ? err.message : err) || 'Something went wrong';
      if (isRetryableAiError(message) && attempt < RETRY_DELAYS_MS.length) {
        console.warn(`[VoiceAI] network exception on attempt ${attempt + 1}, retrying:`, redactSecrets(message));
        lastDetail = message;
        await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
        continue;
      }
      console.error('[VoiceAI] Unexpected error:', redactSecrets(message));
      return { transcript: '', reply: '', error: redactSecrets(message) };
    }
  }

  console.error('[VoiceAI] all retries exhausted, last error:', redactSecrets(lastDetail));
  return { transcript: '', reply: '', error: friendlyAiBusyMessage(history) };
}

/**
 * Play a base64-encoded MP3 audio string through the browser's audio API.
 * Returns a Promise that resolves when playback finishes.
 */
export function playAudioBase64(base64: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const audio = new Audio(`data:audio/mp3;base64,${base64}`);
      audio.onended = () => resolve();
      audio.onerror = (e) => reject(e);
      audio.play().catch(reject);
    } catch (err) {
      reject(err);
    }
  });
}
